import {AfterViewInit, Component, effect, ElementRef, HostBinding, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {toObservable} from '@angular/core/rxjs-interop';
import {distinctUntilChanged, map, Subscription} from 'rxjs';
import lottie, {AnimationItem} from 'lottie-web';
import {MatBottomSheet, MatBottomSheetRef} from '@angular/material/bottom-sheet';
import {MatList} from '@angular/material/list';
import {MatSlideToggle} from '@angular/material/slide-toggle';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {OpenaiService} from '../../../api/services/openai.service';
import {CdkTextareaAutosize} from '@angular/cdk/text-field';
import {CytoscapeService} from '../../../services/cy-services/cytoscape.service';
import {MapService} from '../../../api/services/map.service';
import {MatTooltip} from '@angular/material/tooltip';
import {MarkdownComponent} from 'ngx-markdown';
import {ChatMessage, ChatMessageSender, PendingAction, Suggestion} from '../../../api/interfaces/thread-messages.dto';
import {NodeService} from '../../../api/services/node.service';
import {ModelService} from '../../../api/services/model.service';
import {AiStateService} from '../../../services/ai-state.service';
import {GenerationStateService} from '../../../services/generation-state.service';
import {MapRoleService} from '../../../acl/map-role.service';
import {TrialStateService} from '../../../services/trial-state.service';
import {environment} from '../../../../environments/environment';
import {UserService} from '../../../api/services/user.service';
import {JobService} from '../../../api/services/job.service';
import {Job} from '../../../api/interfaces/jobs.dto';
import {get} from 'lodash';
import {MarkdownService} from '../../helpers/markdown.service';
import {isColorLight} from '../../helpers/color-helpers';
import {CyPreviewComponent} from '../cy-preview/cy-preview.component';
import {NotificationService} from '../../../services/notification.service';
import {MapScreenshotService} from '../../../services/map-screenshot.service';
import {PackageService} from '../../../api/services/package.service';
import {PACKAGE_NAMES} from '@maporium-workspace/shared';
import {MatDialog} from '@angular/material/dialog';
import {Router} from '@angular/router';
import {CreditApiService} from '../../../api/services/credit.service';
import {CreateNewMapDialogComponent} from '../../../dialogs/create-new-map-dialog.component';
import {AiUsageDialogComponent} from '../../../dialogs/ai-usage-dialog.component';
import {UserBalanceDetail} from '../../../api/interfaces/credit.interface';
import {DashboardComponent} from '../../../dashboard/dashboard.component';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';

interface ChangeCount {
  nodes: number;
  links: number;
}

interface ChangeCounts {
  created: ChangeCount;
  updated: ChangeCount;
  deleted: ChangeCount;
}

@Component({
  selector: 'app-chat-bottomsheet',
  standalone: true,
  styleUrls: ['./ai-chat-dialog.component.scss'],
  template: `
    <!-- Response bubble (collapsed + bubble visible) -->
    @if (isCollapsed && showBubble && bubbleMessages.length) {
      <div class="response-bubble-container">
        <div class="response-bubble-popup">
          <div class="bubble-scroll">
            @for (msg of bubbleMessages; track msg) {
              <div class="response-container" [class.streaming]="msg.isStreaming">
                <markdown [data]="cleanMarkdownText(msg.text)" class="response-markdown"></markdown>
              </div>
            }
            @if (latestResponseHasAction && !isLoading) {
              <button (click)="applyBubbleAction()"
                      class="dialog-action-btn dialog-action-btn-primary mt-2">
                {{ latestAssistantMessage!.isCanvas ? ('AI_ASSISTANT.ADD_TO_SMAP' | translate) : ('AI_ASSISTANT.UPDATE_SMAP' | translate) }}
              </button>
            }
            @if (latestAssistantMessage?.applied) {
              <span class="applied-label mt-2">{{ 'AI_ASSISTANT.SMAP_UPDATED' | translate }}</span>
            }
          </div>
          <div class="bubble-tip"></div>
        </div>
      </div>
    }

    <!-- Header (always visible) - matches common bottom sheet header pattern -->
    <div class="d-flex align-items-center dialog-header">
      <h2 class="align-items-center d-flex m-0 p-0">
        @if (isLoading && isCollapsed) {
          <div class="header-lottie me-2" #headerLottie></div>
        } @else {
          <span #assistantIcon
                class="icon-smap-function-assistant me-2"
                [class.bubble-active]="isCollapsed && showBubble"
                (click)="onAssistantIconClick()"
                [style.cursor]="isCollapsed ? 'pointer' : 'default'"
                [matTooltip]="isCollapsed ? (showBubble ? ('AI_ASSISTANT.HIDE_LAST_RESPONSE' | translate) : ('AI_ASSISTANT.SHOW_LAST_RESPONSE' | translate)) : ''"></span>
        }
        {{ 'AI_ASSISTANT.TITLE' | translate }}
      </h2>
      @if (isCollapsed) {
        <span class="header-middle">
          @if (isLoading) {
            <span class="collapsed-loading">
              <span class="loading-dot"></span>
              <span class="loading-dot"></span>
              <span class="loading-dot"></span>
            </span>
          }
          <span class="credits-used credits-used--collapsed"
                [matTooltip]="'MAP_LIST.AI_CREDITS_TOOLTIP' | translate">
            {{ creditsUsed | number:'1.0-0' }} {{ 'AI_ASSISTANT.CREDITS_USED_LABEL' | translate }}
          </span>
        </span>
      }
      <span class="{{ isCollapsed ? 'icon-smap-action-collapse' : 'icon-smap-action-expand' }} close-button ms-auto"
            (click)="toggleCollapse()"
            [matTooltip]="isCollapsed ? ('AI_ASSISTANT.MAXIMIZE' | translate) : ('AI_ASSISTANT.MINIMIZE' | translate)"></span>
      <span class="icon-smap-action-close close-button"
            (click)="close()"
            [matTooltip]="'AI_ASSISTANT.CLOSE' | translate"></span>
    </div>

    <!-- Action header -->
    <div class="assistant-header" [class.d-none]="isCollapsed">
      <button class="dialog-action-btn dialog-action-btn-primary"
              (click)="openCreateSmapDialog()"
              [matTooltip]="'AI_ASSISTANT.CREATE_SMAP_TOOLTIP' | translate">
        {{ 'MAP_LIST.CREATE_SMAP' | translate }}
      </button>
      <span class="credits-used credits-used--expanded"
            [matTooltip]="'MAP_LIST.AI_CREDITS_TOOLTIP' | translate">
        {{ creditsUsed | number:'1.0-0' }} {{ 'AI_ASSISTANT.CREDITS_USED_LABEL' | translate }}
      </span>

      <button class="credits-balance-btn"
              [class.overdrawn]="creditBalance < 0"
              (click)="openAiUsageDialog()"
              [matTooltip]="'AI_ASSISTANT.MANAGE_CREDITS_TOOLTIP' | translate">
        {{ creditBalance >= 0 ? (creditBalance | number:'1.0-0') : (creditBalance * -1 | number:'1.0-0') }}
        {{ creditBalance >= 0 ? ('AI_ASSISTANT.REMAINING_CREDITS' | translate) : ('AI_ASSISTANT.OVERDRAWN_CREDITS' | translate) }}
      </button>
    </div>

    <div class="bottomsheet-content" [class.d-none]="isCollapsed">
      <div #messageList class="message-list scroll-container">
        <mat-list class="message-list">
        <div *ngFor="let message of messages; let i = index" class="message-row" [ngClass]="{'user-row': message.isUsers, 'assistant-row': !message.isUsers}">
          @if(message.isCanvas) {
            <div class="avatar assistant-avatar">
              <span class="icon-smap-function-assistant"></span>
            </div>
            <div class="message-bubble canvas-bubble">
              <div class="message-content">
                <cy-preview
                  class="cy-preview"
                  [nodes]="message.data?.nodes || []"
                  [mapStyles]="currentMapStyles"
                  [disableSnapshot]="true">
                </cy-preview>
                <button *ngIf="canInsertInSmap() && !message.applied"
                        (click)="add(i)"
                        [matTooltip]="'AI_ASSISTANT.ADD_ELEMENTS_TOOLTIP' | translate"
                        class="dialog-action-btn dialog-action-btn-primary insert-btn">
                  {{ 'AI_ASSISTANT.ADD_TO_SMAP' | translate }}
                </button>
                <span *ngIf="message.applied && message.isCanvas" class="applied-label">{{ 'AI_ASSISTANT.ADDED_TO_SMAP' | translate }}</span>
              </div>
            </div>
          } @else {
            @if(!message.isUsers) {
              <div class="avatar assistant-avatar">
                @if (message.isProcessing || message.isStreaming) {
                  <div class="avatar-lottie" [attr.data-msg-index]="i"></div>
                } @else {
                  <span class="icon-smap-function-assistant"></span>
                }
              </div>
            }
            <div class="message-bubble"
                 [ngClass]="{'user-bubble': message.isUsers, 'assistant-bubble': !message.isUsers, 'error-bubble': message.isError}">
              <div class="message-content">
                @if(message.isUsers) {
                  <div class="user-text">{{ message.text }}</div>
                } @else if (message.isProcessing) {
                  <div class="response-container processing-message">
                    <div class="processing-title">{{ message.text }}</div>
                    <div class="processing-hint" [class.visible]="showProcessingHint">
                      {{ 'AI_ASSISTANT.PROCESSING_HINT' | translate }}
                    </div>
                  </div>
                } @else {
                  <div class="response-container" [class.streaming]="message.isStreaming">
                    <markdown [data]="cleanMarkdownText(message.text)" class="response-markdown"></markdown>
                    <button *ngIf="hasPendingAction(message) && !message.applied"
                            (click)="applyPendingAction(i)"
                            [matTooltip]="'AI_ASSISTANT.APPLY_SUGGESTED_CHANGES' | translate"
                            class="dialog-action-btn dialog-action-btn-primary mt-2">
                      {{ 'AI_ASSISTANT.UPDATE_SMAP' | translate }}
                    </button>
                    <span *ngIf="message.applied" class="applied-label mt-2">{{ 'AI_ASSISTANT.SMAP_UPDATED' | translate }}</span>
                  </div>
                }
              </div>
            </div>
            @if(message.isUsers) {
              <div class="avatar user-avatar"
                   [ngStyle]="getUserAvatarStyle(message)"
                   [matTooltip]="getSenderTooltip(message)"
                   matTooltipPosition="left">
                {{ getUserInitials(message) }}
              </div>
            }
          }
        </div>
      </mat-list>
      </div>
    </div>

    <div class="input-section">
      <div class="input-row">
        <div class="input-container">
          <textarea
            class="message-input"
            #promptInput
            [(ngModel)]="newMessage"
            (keydown.enter)="onEnterKey($event)"
            [disabled]="isLoading"
            [placeholder]="'AI_ASSISTANT.INPUT_PLACEHOLDER' | translate"
            rows="1"
            cdkTextareaAutosize
            cdkAutosizeMinRows="1"
            cdkAutosizeMaxRows="9"></textarea>
        </div>
        <button class="dialog-action-btn dialog-action-btn-primary send-btn"
                (click)="sendMessage()"
                [disabled]="isLoading || !newMessage.trim()"
                [matTooltip]="'AI_ASSISTANT.SEND_TOOLTIP' | translate">
          {{ 'AI_ASSISTANT.SEND' | translate }}
        </button>
        @if (webSearchAvailable) {
          <mat-slide-toggle
            class="web-search-toggle"
            [class.is-disabled]="webFeaturesLocked"
            [(ngModel)]="webSearchEnabled"
            [disabled]="webFeaturesLocked"
            labelPosition="before"
            [matTooltip]="'AI_ASSISTANT.SEARCH_WEB_TOOLTIP' | translate">
            {{ 'CREATE_MAP_DIALOG.SEARCH_WEB' | translate }}
          </mat-slide-toggle>
        }
        <mat-slide-toggle
          class="preview-toggle"
          [(ngModel)]="previewMode"
          (ngModelChange)="saveGlobalState()"
          labelPosition="before"
          [matTooltip]="'AI_ASSISTANT.PREVIEW_CHANGES_TOOLTIP' | translate">
          {{ 'AI_ASSISTANT.PREVIEW_CHANGES' | translate }}
        </mat-slide-toggle>
      </div>
    </div>
  `,
  imports: [
    MatList,
    CommonModule,
    FormsModule,
    CdkTextareaAutosize,
    MatTooltip,
    MarkdownComponent,
    CyPreviewComponent,
    MatSlideToggle,
    TranslatePipe
  ],
  styles: [`
    .sent-message {
      justify-content: flex-end;
    }

    .received-message {
      justify-content: flex-start;
    }

    .user-message {
      word-wrap: break-word;
      overflow-wrap: break-word;
    }

    .user-text {
      white-space: pre-wrap;
      word-wrap: break-word;
      overflow-wrap: break-word;
      max-width: 100%;
      overflow-x: auto;
    }

    .assistant-message {
      word-wrap: break-word;
      overflow-wrap: break-word;
    }

    .markdown-content {
      max-width: 100%;
      width: 100%;
      overflow-x: auto;
    }

    .markdown-content markdown {
      display: block;
      width: 100%;
      max-width: 100%;
    }

    .markdown-content ::ng-deep p,
    .markdown-content ::ng-deep ol,
    .markdown-content ::ng-deep ul,
    .markdown-content ::ng-deep li,
    .markdown-content ::ng-deep h1,
    .markdown-content ::ng-deep h2,
    .markdown-content ::ng-deep h3,
    .markdown-content ::ng-deep h4,
    .markdown-content ::ng-deep h5,
    .markdown-content ::ng-deep h6 {
      word-wrap: break-word !important;
      overflow-wrap: anywhere !important;
      word-break: break-word !important;
      margin-bottom: 0.5rem;
      max-width: 100% !important;
      width: 100% !important;
    }

    .markdown-content ::ng-deep pre {
      white-space: pre-wrap;
      word-wrap: break-word;
      overflow-wrap: break-word;
      max-width: 100%;
      overflow-x: auto;
    }

    .markdown-content ::ng-deep code {
      word-wrap: break-word;
      overflow-wrap: break-word;
      max-width: 100%;
    }

    .markdown-content ::ng-deep strong {
      word-wrap: break-word;
      overflow-wrap: break-word;
    }
  `]
})
export class ChatBottomsheetComponent implements OnInit, OnDestroy, AfterViewInit {
  private static readonly STATE_KEY = 'maporium_chat_state';
  private static readonly PROMPT_KEY_PREFIX = 'maporium_chat_prompt_';
  private static readonly APPLIED_KEY_PREFIX = 'maporium_chat_applied_';
  private static readonly APPLIED_MAX = 50;
  private static readonly LOTTIE_PATH = 'assets/lottie/symbol-assistant.json';
  private lottieInstances = new Map<string, AnimationItem>();

  messages: ChatMessage[] = [];
  newMessage = '';
  isLoading = false;
  hasPreview = false;
  previewMode = true;
  webSearchEnabled = false;
  isCollapsed = false;
  showBubble = false;

  @HostBinding('class.collapsed') get collapsedClass() {
    return this.isCollapsed;
  }

  /** MAP-1607: web search is withheld for the length of the free trial, silently here. */
  get webFeaturesLocked(): boolean {
    return this.trialStateService.isOnTrial;
  }

  get webSearchAvailable(): boolean {
    return !environment.production || !this.aiStateService.isUsingCustomKeys();
  }

  get latestAssistantMessage(): ChatMessage | null {
    for (let i = this.messages.length - 1; i >= 0; i--) {
      const msg = this.messages[i];
      if (!msg.isUsers && !msg.isProcessing) {
        return msg;
      }
    }
    return null;
  }

  /**
   * Assistant messages to render in the collapsed-state response bubble.
   * The bubble accumulates responses: each new streamed response appends below
   * the previous ones inside the same DOM, instead of replacing them. The
   * "Your request is being processed..." system messages (isProcessing) are
   * filtered out — those should never surface in the bubble.
   */
  get bubbleMessages(): ChatMessage[] {
    return this.messages.filter(m => !m.isUsers && !m.isProcessing);
  }

  get latestResponseHasAction(): boolean {
    const msg = this.latestAssistantMessage;
    if (!msg || msg.applied) return false;
    if (msg.isCanvas) return this.canInsertInSmap();
    return !!msg.pendingActions?.length;
  }

  private get latestAssistantIndex(): number {
    for (let i = this.messages.length - 1; i >= 0; i--) {
      if (!this.messages[i].isUsers && !this.messages[i].isProcessing) {
        return i;
      }
    }
    return -1;
  }

  currentMapStyles: any;
  showProcessingHint = false;
  creditBalance = 0;
  creditsUsed = 0;

  @ViewChild('promptInput') promptInputRef!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('messageList') messageListRef!: ElementRef<HTMLDivElement>;
  @ViewChild('assistantIcon', {read: ElementRef}) assistantIconRef?: ElementRef<HTMLElement>;

  private pendingJobId: string | null = null;
  private isDialogClosing = false;
  private hintTimeout: any = null;
  private sourceMapId: string | null = null;
  private autoSubmitSubscription: Subscription | undefined;
  private balanceSubscription: Subscription | undefined;
  private mapChangeSubscription: Subscription | undefined;
  /** In the constructor: toObservable() requires an injection context. */
  private readonly selectedMapId$ = toObservable(this.mapService.selectedMap).pipe(
    map((m) => m?.id),
    distinctUntilChanged()
  );
  private initialMessagesLoaded = false;
  private queuedAutoPrompt: string | null = null;

  constructor(
      private bottomSheetRef: MatBottomSheetRef<ChatBottomsheetComponent>,
    private cyService: CytoscapeService,
    private mapService: MapService,
      private nodeService: NodeService,
    private userService: UserService,
    private generativeService: OpenaiService,
    private jobService: JobService,
    private notificationService: NotificationService,
      private bottomSheet: MatBottomSheet,
      private packageService: PackageService,
      private creditApiService: CreditApiService,
      private dialog: MatDialog,
      private screenshotService: MapScreenshotService,
      private router: Router,
      private modelService: ModelService,
      private aiStateService: AiStateService,
      private generationStateService: GenerationStateService,
      private translate: TranslateService,
      private hostRef: ElementRef<HTMLElement>,
      private trialStateService: TrialStateService,
      private mapRoleService: MapRoleService
  ) {
    this.loadGlobalState();
    // MAP-147: the user can switch AI off from the usage dialog while this is
    // open. A request already running is left to finish in the background.
    effect(() => {
      if (!this.aiStateService.aiFeaturesVisible()) {
        this.bottomSheetRef.dismiss();
      }
    });
    // Toolbar lottie watches this — true while drawer is mounted (MAP-1402).
    this.generationStateService.setAssistantOpen(true);
  }

  ngAfterViewInit() {
    // Measure icon position as soon as the header is laid out; the scroll +
    // focus pass happens later on the 1100ms delay for the history load.
    setTimeout(() => this.updateBubbleTipPosition(), 0);
    setTimeout(() => {
      if (this.promptInputRef?.nativeElement) {
        this.promptInputRef.nativeElement.focus();
      }
      this.scrollToBottom();
      this.updateBubbleTipPosition();
    }, 1100);
  }

  /**
   * Align the response bubble's tip with the smap assistant icon. The icon's
   * horizontal position isn't known statically (depends on dialog padding,
   * icon font metrics, and whether the lottie animation is active), so we
   * measure at runtime and publish the result as a CSS custom property the
   * stylesheet consumes.
   */
  private updateBubbleTipPosition(): void {
    const icon = this.assistantIconRef?.nativeElement;
    if (!icon || !this.hostRef?.nativeElement) return;
    const iconRect = icon.getBoundingClientRect();
    const hostRect = this.hostRef.nativeElement.getBoundingClientRect();
    // Target: place the tip's triangle point under the icon center.
    // The tip sits inside .response-bubble-popup, which is inset 8px from
    // the host (container's padding-left), and the tip itself is 16px wide
    // (8px half). So `tip.left` (relative to popup) = iconCenterFromHost - 16.
    const iconCenterFromHost = iconRect.left + iconRect.width / 2 - hostRect.left;
    const tipLeft = Math.round(iconCenterFromHost - 16);
    this.hostRef.nativeElement.style.setProperty('--bubble-tip-left', `${tipLeft}px`);
  }

  private initLottieAnimations(): void {
    // Defer to next frame so Angular has finished rendering the containers
    setTimeout(() => {
      this.destroyLottieInstances();

      const containers = document.querySelectorAll(
        'app-chat-bottomsheet .header-lottie, app-chat-bottomsheet .avatar-lottie'
      );
      containers.forEach((el: Element, idx) => {
        const inst = lottie.loadAnimation({
          container: el as HTMLElement,
          renderer: 'svg',
          loop: true,
          autoplay: true,
          path: ChatBottomsheetComponent.LOTTIE_PATH,
        });
        this.lottieInstances.set('lottie-' + idx, inst);
      });
    });
  }

  private destroyLottieInstances(): void {
    this.lottieInstances.forEach(inst => inst.destroy());
    this.lottieInstances.clear();
  }

  /**
   * Checks if the user can insert minismaps into the current smap.
   * All plans except Explorer can insert minismaps.
   */
  canInsertInSmap(): boolean {
    return this.packageService.hasPackage(PACKAGE_NAMES.creator) ||
      this.packageService.hasPackage(PACKAGE_NAMES.publisher) ||
      this.packageService.hasPackage(PACKAGE_NAMES.analyst) ||
      this.packageService.hasPackage(PACKAGE_NAMES.developer);
  }

  close(): void {
    this.bottomSheetRef.dismiss();
  }

  toggleCollapse(): void {
    this.isCollapsed = !this.isCollapsed;
    if (!this.isCollapsed) {
      // Hide bubble when expanding (AC 2.d)
      this.showBubble = false;
      // Scroll to bottom when expanding
      this.scrollToBottom();
    }
    this.saveGlobalState();
    // Header layout changes (credits counter appears/disappears) — re-measure.
    setTimeout(() => this.updateBubbleTipPosition(), 0);
    // The @if on (isLoading && isCollapsed) just added/removed the header-lottie
    // div; and the avatar-lottie containers are inside bottomsheet-content which
    // is d-none'd when collapsed. Either way the DOM set for lottie changed —
    // re-init so whichever lottie is now visible actually plays (MAP-1402).
    if (this.isLoading) {
      this.initLottieAnimations();
    }
  }

  toggleBubble(): void {
    this.showBubble = !this.showBubble;
    this.saveGlobalState();
  }

  onAssistantIconClick(): void {
    if (this.isCollapsed) {
      this.toggleBubble();
    }
  }

  // Capture phase so canvas/sidebar handlers that stopPropagation can't
  // swallow the event before it reaches us; the minimized sheet's backdrop is
  // click-through (MAP-1475), so outside interactions land on app elements.
  private onDocumentPointerDown = (event: Event): void => {
    if (!this.isCollapsed || !this.showBubble) return;
    if (this.hostRef.nativeElement.contains(event.target as Node)) return;
    this.showBubble = false;
    this.saveGlobalState();
  };

  onEnterKey(event: Event): void {
    // Prevent newline insertion (AC 8)
    event.preventDefault();
    this.sendMessage();
  }

  applyBubbleAction(): void {
    const idx = this.latestAssistantIndex;
    if (idx < 0) return;
    const msg = this.messages[idx];
    if (msg.isCanvas) {
      this.add(idx);
    } else if (msg.pendingActions?.length) {
      this.applyPendingAction(idx);
    }
  }

  saveGlobalState(): void {
    localStorage.setItem(ChatBottomsheetComponent.STATE_KEY, JSON.stringify({
      collapsed: this.isCollapsed,
      previewMode: this.previewMode,
      showBubble: this.showBubble
    }));
  }

  private loadGlobalState(): void {
    try {
      const raw = localStorage.getItem(ChatBottomsheetComponent.STATE_KEY);
      if (raw) {
        const state = JSON.parse(raw);
        this.isCollapsed = state.collapsed ?? false;
        this.previewMode = state.previewMode ?? true;
        this.showBubble = state.showBubble ?? false;
      }
    } catch { /* ignore corrupted state */
    }
  }

  private savePrompt(): void {
    const mapId = this.mapService.getCurrentSelectedMapFromStore()?.id;
    if (mapId && this.newMessage.trim()) {
      localStorage.setItem(ChatBottomsheetComponent.PROMPT_KEY_PREFIX + mapId, this.newMessage);
    } else if (mapId) {
      localStorage.removeItem(ChatBottomsheetComponent.PROMPT_KEY_PREFIX + mapId);
    }
  }

  private clearSavedPrompt(): void {
    const mapId = this.mapService.getCurrentSelectedMapFromStore()?.id;
    if (mapId) {
      localStorage.removeItem(ChatBottomsheetComponent.PROMPT_KEY_PREFIX + mapId);
    }
  }

  /**
   * Track which assistant messages have been applied (auto-apply when
   * preview=off, or manual UPDATE SMAP click). Persisted per-map so the
   * "Smap updated." marker survives close/reopen and a full reload —
   * otherwise extractPendingActionsFromContent would re-attach an UPDATE SMAP
   * button on the same response after a session boundary (MAP-1443).
   */
  private appliedKeyForCurrentMap(): string | null {
    const id = this.mapService.getCurrentSelectedMapFromStore()?.id;
    return id ? ChatBottomsheetComponent.APPLIED_KEY_PREFIX + id : null;
  }

  private loadAppliedList(): string[] {
    try {
      const key = this.appliedKeyForCurrentMap();
      if (!key) return [];
      const raw = localStorage.getItem(key);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private markMessageApplied(text: string | undefined | null): void {
    if (!text) return;
    const key = this.appliedKeyForCurrentMap();
    if (!key) return;
    const list = this.loadAppliedList();
    if (list.includes(text)) return;
    list.push(text);
    while (list.length > ChatBottomsheetComponent.APPLIED_MAX) list.shift();
    try {
      localStorage.setItem(key, JSON.stringify(list));
    } catch {
      // Storage full or denied — silently fall back to in-memory only.
    }
  }

  private isMessageApplied(text: string | undefined | null): boolean {
    if (!text) return false;
    return this.loadAppliedList().includes(text);
  }

  openCreateSmapDialog(): void {
    const ref = this.dialog.open(CreateNewMapDialogComponent, {
      width: '500px'
    });
    // A credit purchase dialog can be opened from inside (via ensureCredits)
    // before the generation starts. Refresh once the dialog chain closes so
    // the assistant's remaining credits stay accurate.
    ref.afterClosed().subscribe(() => this.creditApiService.refreshBalance());
  }

  openAiUsageDialog(): void {
    const ref = this.dialog.open(AiUsageDialogComponent, {
      width: '50%',
      autoFocus: false,
    });
    // Credits can be purchased from inside this dialog. Refresh on close
    // so the header reflects the new balance (AC 4.4).
    ref.afterClosed().subscribe(() => this.creditApiService.refreshBalance());
  }

  /** `mapId` is passed explicitly so a lagging route param cannot stale it. */
  private loadCreditBalance(mapId?: string): void {
    this.creditApiService.refreshBalance();
    const currentMapId = mapId ?? this.mapService.getCurrentSelectedMapFromStore()?.id;
    if (currentMapId) {
      this.mapService.getAiUsage(currentMapId).subscribe(({aiCreditsUsed}) => {
        this.creditsUsed = aiCreditsUsed;
      });
    } else {
      this.creditsUsed = 0;
    }
  }

  /**
   * Decide whether the current user question plausibly needs a map screenshot.
   * Attaching the screenshot costs ~1500-2500 vision input tokens per turn and
   * slows prefill, so we skip it for plain text Q&A and only attach when the
   * wording suggests layout, positioning, or visual reasoning is involved.
   * The backend still has full node/link/position data via the attached map
   * document, so skipping the screenshot is safe for non-visual questions.
   */
  private questionNeedsVisualContext(question: string): boolean {
    const q = question.toLowerCase();
    // Clear visual/layout intent: attach screenshot
    const visualIntent = /\b(rearrange|reorganiz|reposition|re-?position|arrange|layout|spread|scatter|cluster|overlap|hierarchy|radial|concentric|fcose|dagre|cose-bilkent|where\s+(is|are)|move\s+(the\s+)?node|place\s+(the\s+)?node|visual|look\s+at|see\s+the\s+map|on\s+the\s+(left|right|top|bottom)|in\s+the\s+center)\b/;
    if (visualIntent.test(q)) return true;

    // Clear text-only intent: skip screenshot
    const textOnly = /^\s*(what\s+(is|are|'s|does|do)|who\s+(is|are)|why|explain|describe|tell\s+me|define|list|summari[sz]e|how\s+does)\b/;
    if (textOnly.test(q)) return false;

    // Default: attach (preserves current behavior for ambiguous cases like
    // "add 5 nodes about X" where placement may benefit from visual context).
    return true;
  }

  get userName() {
    const user = this.userService.getCurrentUserFromStorage();
    return user.name + ' ' + user.lastName;
  }

  getUserInitials(message?: ChatMessage): string {
    const sender = this.getMessageSender(message);
    const firstInitial = sender.name ? sender.name.charAt(0).toUpperCase() : '';
    const lastInitial = sender.lastName ? sender.lastName.charAt(0).toUpperCase() : '';
    return firstInitial + lastInitial;
  }

  getUserAvatarStyle(message?: ChatMessage): { [key: string]: string } {
    const sender = this.getMessageSender(message);
    const backgroundColor = sender?.color || '#597080';
    const textColor = isColorLight(backgroundColor) ? '#000000' : '#ffffff';

    return {
      'background-color': backgroundColor,
      'color': textColor
    };
  }

  getSenderTooltip(message: ChatMessage): string {
    const sender = message?.sender;
    if (!sender) return '';
    const currentUserId = this.userService.getCurrentUserFromStorage()?.id;
    if (sender.id === currentUserId) return '';
    const fullName = `${sender.name || ''} ${sender.lastName || ''}`.trim();
    const org = sender.organization ? ` (${sender.organization})` : '';
    const bio = sender.biography ? `: ${sender.biography}` : '';
    return `${fullName}${org}${bio}`;
  }

  private getMessageSender(message?: ChatMessage): { name: string; lastName: string; color?: string } {
    if (message?.sender) return message.sender;
    return this.userService.getCurrentUserFromStorage();
  }

  private currentUserAsSender(): ChatMessageSender {
    const u = this.userService.getCurrentUserFromStorage();
    return {
      id: u?.id,
      name: u?.name,
      lastName: u?.lastName,
      organization: u?.organization,
      biography: u?.biography,
      color: u?.color
    };
  }

  ngOnInit(): void {
    document.addEventListener('pointerdown', this.onDocumentPointerDown, true);

    this.trialStateService.refresh();
    if (this.webFeaturesLocked) {
      this.webSearchEnabled = false;
    }

    // Get current map styles for preview component
    const currentMap = this.mapService.getCurrentSelectedMapFromStore();
    this.currentMapStyles = currentMap?.styles;

    // Keep the header's remaining credits in sync with balance changes that
    // originate outside the assistant: smap generation from CREATE SMAP,
    // credit purchases, ensureCredits checks, etc. (AC 4.x)
    this.balanceSubscription = this.creditApiService.balanceChanged$.subscribe(
      (balance: UserBalanceDetail) => {
        this.creditBalance = balance.currentBalance;
      }
    );

    this.loadCreditBalance();

    // MAP-1618: the assistant survives a smap switch, so EVERYTHING it shows has
    // to switch with it, not just the credits count. The chat contents belong to
    // the smap, and so does the in-progress state: a request still running on the
    // smap we just left must not keep this smap's assistant animating.
    this.mapChangeSubscription = this.selectedMapId$
      .subscribe((mapId) => {
        if (!mapId) return;
        // MAP-71: the smap switched to may be view-only, and viewers and
        // commenters get no assistant. A request still running on the smap we
        // left keeps the toolbar icon animating, disabled.
        if (!this.mapRoleService.canUseAssistant()) {
          this.bottomSheetRef.dismiss();
          return;
        }
        this.loadCreditBalance(mapId);
        this.syncLoadingToCurrentMap(mapId);
        this.loadChatForMap(mapId);
      });

    // Listen for prompts submitted programmatically (e.g. legend GENERATE button).
    // If the initial history hasn't loaded yet, queue the prompt — otherwise the
    // setTimeout below replaces this.messages and wipes the pending request.
    this.autoSubmitSubscription = this.aiStateService.autoSubmitPrompt$.subscribe((prompt) => {
      if (!prompt) return;
      if (!this.initialMessagesLoaded) {
        this.queuedAutoPrompt = prompt;
        return;
      }
      this.dispatchAutoPrompt(prompt);
    });

    // Restore per-smap prompt
    if (currentMap?.id) {
      const savedPrompt = localStorage.getItem(ChatBottomsheetComponent.PROMPT_KEY_PREFIX + currentMap.id);
      if (savedPrompt) {
        this.newMessage = savedPrompt;
      }
    }

    this.loadChatForMap(currentMap.id);
  }

  /**
   * MAP-1618: the head and three-dot animations report work on the smap on
   * screen, so switching smaps re-derives them from that smap rather than
   * leaving the previous smap's request spinning here.
   */
  private syncLoadingToCurrentMap(mapId: string): void {
    const running = this.generationStateService.isAssistantRunningForMap(mapId);
    if (this.isLoading === running) return;
    this.isLoading = running;
    if (!running) {
      this.pendingJobId = null;
    }
  }

  /**
   * MAP-1618: chat contents belong to the smap, so this runs on open AND on every
   * smap switch. Resets the visible thread, then re-attaches to a request already
   * running on the smap being switched to.
   */
  private loadChatForMap(mapId: string): void {
    this.initialMessagesLoaded = false;
    setTimeout(() => {
      this.generativeService
        .getMessagesByMap(mapId, 30)
        .subscribe((res) => {
          // Parse JSON strings in message text fields and transform data for preview
          this.messages = res.map(message => {
            const result: ChatMessage = {
              ...message,
              text: this.parseMessageText(message.text)
            };
            // Links-only responses (no nodes) were applied directly — show as text, not canvas
            if (message.isCanvas && message.data && (!message.data.nodes || message.data.nodes.length === 0)) {
              result.isCanvas = false;
              result.text = result.text || this.translate.instant('AI_ASSISTANT.CONNECTED_LINKS');
              result.data = undefined;
            }
            // Transform data for canvas messages (historical messages need transformation too)
            else if (message.isCanvas && message.data?.links) {
              result.data = this.transformDataForPreview(message.data);
            }
            // MAP-1443: rehydrate applied state for messages that were already
            // applied in a prior session (preview=off auto-apply, manual click,
            // or close-mid-stream silent apply). Otherwise the UPDATE SMAP /
            // ADD TO SMAP button would re-appear on a response that was already
            // applied to the smap.
            if (!message.isUsers && this.isMessageApplied(result.text)) {
              result.applied = true;
            }
            return result;
          });

          // Restore pending actions on the most recent assistant message so the
          // UPDATE SMAP button reappears when the dialog is reopened after the
          // job completed in the background while preview was on. Skip when
          // the message is already marked applied (MAP-1443) — re-attaching the
          // action would falsely invite the user to apply changes that already
          // landed on the smap.
          const lastIndex = res.length - 1;
          if (lastIndex >= 0 && !res[lastIndex].isUsers && !this.messages[lastIndex].applied) {
            const pending = this.extractPendingActionsFromContent(res[lastIndex].text);
            if (pending?.length) {
              this.messages[lastIndex].pendingActions = pending;
            }
          }

          this.scrollToBottom();

          // MAP-1443: if a job was already in flight on this smap when the
          // assistant was closed, take it over here so the user sees the
          // streaming response and the completion handler runs in this live
          // instance (instead of the destroyed one). JobService's tracking
          // token guarantees the previous instance's polling is shed.
          this.rehydrateInFlightJob(mapId);

          // History is in place — safe to dispatch any queued auto-prompt without
          // it being clobbered by this subscribe callback.
          this.initialMessagesLoaded = true;
          if (this.queuedAutoPrompt) {
            const prompt = this.queuedAutoPrompt;
            this.queuedAutoPrompt = null;
            this.dispatchAutoPrompt(prompt);
          }
        });
    }, 300);
  }

  private rehydrateInFlightJob(mapId: string): void {
    const activeJobId =
      this.generationStateService.getActiveAssistantJobIdForMap(mapId);
    if (!activeJobId) return;

    this.sourceMapId = mapId;
    this.pendingJobId = activeJobId;
    this.isLoading = true;

    const placeholderIndex = this.messages.length;
    this.messages.push({
      text: this.translate.instant('AI_ASSISTANT.REQUEST_PROCESSING'),
      isSent: false,
      isCanvas: false,
      isUsers: false,
      isProcessing: true
    });

    this.scrollToBottom();
    this.initLottieAnimations();

    if (this.isCollapsed) {
      this.showBubble = true;
      this.saveGlobalState();
    }

    this.listenToJobUpdates(activeJobId, placeholderIndex);
  }

  private dispatchAutoPrompt(prompt: string): void {
    this.newMessage = prompt;
    // Expand if collapsed so the user sees what's happening
    if (this.isCollapsed) {
      this.isCollapsed = false;
      this.saveGlobalState();
    }
    this.sendMessage();
  }

  /**
   * Reconstruct pendingActions from the raw JSON content of a stored assistant
   * message. Mirrors the logic in `continueJobCompletion()` so a message loaded
   * from history can render its UPDATE SMAP / apply button when the dialog is
   * reopened (the in-memory `pendingActions` are otherwise lost across closes,
   * and the backend stores only the raw response content).
   */
  private extractPendingActionsFromContent(rawContent: string): PendingAction[] | undefined {
    if (!rawContent || typeof rawContent !== 'string') return undefined;
    let parsed: any;
    try {
      parsed = JSON.parse(rawContent);
    } catch {
      return undefined;
    }
    if (!parsed || typeof parsed !== 'object') return undefined;

    const action = parsed.action;

    // Rearrange (layout algorithm or explicit positions)
    if (action === 'rearrange') {
      if (parsed.layout) {
        return [{type: 'rearrange_layout', payload: parsed.layout}];
      }
      if (parsed.positions?.length) {
        return [{type: 'rearrange', payload: parsed.positions}];
      }
    }

    // Deduplicate
    if (action === 'deduplicate') {
      return [{type: 'deduplicate', payload: null}];
    }

    // Create models for legend
    if (action === 'create-models') {
      return [{type: 'create-models', payload: null}];
    }

    // Delete / recreate legend models
    if (action === 'delete-models') {
      return [{type: 'delete-models', payload: null}];
    }
    if (action === 'recreate-models') {
      return [{type: 'recreate-models', payload: null}];
    }

    // Links-only data response (connecting existing nodes)
    const mapData = parsed.mapData || parsed.data;
    const nodesCount = mapData?.nodes?.length || 0;
    const linksCount = mapData?.links?.length || 0;
    if (parsed.responseType === 'data' && nodesCount === 0 && linksCount > 0) {
      return [{type: 'add_links', payload: mapData.links}];
    }

    // Suggestions (update_node, update_link, update_map, add_elements)
    const suggestions = parsed.suggestions;
    if (action === 'suggest' && Array.isArray(suggestions) && suggestions.length) {
      const actionable = suggestions.filter((s: any) =>
        s.targetId && s.changes && (s.changes.name || s.changes.description || s.changes.imageUrl !== undefined || s.changes.metadata?.length)
      );
      if (actionable.length) {
        return actionable.map((s: Suggestion) => ({
          type: s.type,
          payload: s.changes,
          targetId: s.targetId,
          targetName: s.targetName
        }));
      }
    }

    return undefined;
  }

  sendMessage(): void {
    if (this.newMessage.trim()) {
      const userMessage = this.newMessage;

      this.creditApiService.ensureCredits(this.dialog, 'assistant response').subscribe(ok => {
        if (!ok) return;

        // Add user message to the conversation immediately
        this.messages.push({
          text: userMessage,
          isSent: true,
          isCanvas: false,
          isUsers: true,
          sender: this.currentUserAsSender()
        });

        this.isLoading = true;
        this.initLottieAnimations();
        this.newMessage = ''; // Clear input immediately
        this.clearSavedPrompt(); // Clear saved prompt after sending

        const currentMap = this.mapService.getCurrentSelectedMapFromStore();
        if (!currentMap || !currentMap.id) {
          this.isLoading = false;
          return;
        }
        this.sourceMapId = currentMap.id;

        // Add processing message
        const processingMessageIndex = this.messages.length;
        this.messages.push({
          text: this.translate.instant('AI_ASSISTANT.REQUEST_PROCESSING'),
          isSent: false,
          isCanvas: false,
          isUsers: false,
          isProcessing: true // Add flag for processing state
        });

        this.scrollToBottom();

        // Start timer to show hint after 5 seconds
        this.showProcessingHint = false;
        if (this.hintTimeout) {
          clearTimeout(this.hintTimeout);
        }
        this.hintTimeout = setTimeout(() => {
          this.showProcessingHint = true;
          this.scrollToBottom();
        }, 5000);

        // Compute bounding box center for spatial context
        const cy = this.cyService.cy;
        let mapCenter: { x: number; y: number } | undefined;
        if (cy && cy.nodes().length) {
          const bb = cy.nodes().boundingBox();
          mapCenter = {x: Math.round((bb.x1 + bb.x2) / 2), y: Math.round((bb.y1 + bb.y2) / 2)};
        }

        // Only capture a screenshot when the question plausibly needs visual
        // context. Every screenshot costs ~1500-2500 vision tokens plus extra
        // prefill latency on the backend, and most chat turns are plain Q&A
        // where the map document already carries all the state the model needs.
        const needsScreenshot = this.questionNeedsVisualContext(userMessage);
        const screenshotPromise: Promise<Blob | null> = needsScreenshot
          ? this.screenshotService.captureMap(1)
          : Promise.resolve(null);

        screenshotPromise.then(blob => {
          this.generativeService.generateAnswer(userMessage, currentMap.id, blob || undefined, mapCenter, this.webSearchEnabled).subscribe((job) => {
            this.pendingJobId = job.id;
            // Toolbar lottie subscribes to this when assistant drawer is closed
            // mid-task (MAP-1402).
            this.generationStateService.startAssistantTask(job.id, currentMap.id);
            this.listenToJobUpdates(job.id, processingMessageIndex);
          });
        });
      });
    }
  }


  ngOnDestroy(): void {
    document.removeEventListener('pointerdown', this.onDocumentPointerDown, true);

    // Toolbar lottie picks up the load now that the drawer is gone (MAP-1402).
    this.generationStateService.setAssistantOpen(false);

    // Save per-smap prompt
    this.savePrompt();

    // Hide bubble on close (AC 2.d)
    this.showBubble = false;
    this.saveGlobalState();

    this.autoSubmitSubscription?.unsubscribe();
    this.balanceSubscription?.unsubscribe();
    this.mapChangeSubscription?.unsubscribe();

    // Cleanup lottie animations
    this.destroyLottieInstances();

    // Clear hint timeout
    if (this.hintTimeout) {
      clearTimeout(this.hintTimeout);
      this.hintTimeout = null;
    }

    // If dialog is closed while a job is still processing, continue tracking in background
    if (this.pendingJobId && this.isLoading) {
      // MAP-1579: correlate with the completion notification raised for the
      // same job, so this "still working" toast is torn down if the job
      // finishes while it is still on screen.
      const taskKey = this.pendingJobId;
      this.isDialogClosing = true;
      // The completion handler now silently auto-applies when preview is OFF
      // (MAP-1443), so phrase the close-time toast accordingly.
      const message = this.previewMode
        ? this.translate.instant('AI_ASSISTANT.KEEP_WORKING_NOTIFY')
        : this.translate.instant('AI_ASSISTANT.KEEP_WORKING_AUTO_UPDATE');
      this.notificationService.info(message, {
        duration: 8000,
        hideIcon: true,
        taskKey
      });
      // Polling continues in background
    }
    // No fallback cleanup here: if there is no pending assistant job, there is
    // nothing to cancel, and an unscoped cleanup() would also abort any other
    // tracked generation (e.g. a smap import running in the background). The
    // assistant's own polling auto-cancels on terminal status — see
    // listenToJobUpdates / handleJobCompletion / handleJobFailure (MAP-1317).
  }

  listenToJobUpdates(jobId: string, processingMessageIndex: number): void {
    this.jobService.trackJob(jobId, (job: Job) => {
      if (job.status === 'COMPLETED') {
        this.handleJobCompletion(job, processingMessageIndex);
      } else if (job.status === 'FAILED') {
        this.handleJobFailure(job, processingMessageIndex);
      } else if (job.partialContent) {
        this.handleStreamingUpdate(job.partialContent, processingMessageIndex);
      } else if (job.status === 'IN_PROGRESS' && job.result?.progressMessage) {
        this.handleJobProgress(job, processingMessageIndex);
      }
    });
  }

  handleJobProgress(job: Job, processingMessageIndex: number): void {
    const progressMessage = get(job, 'result.progressMessage', '');

    if (progressMessage === 'smap_changed_syncing') {
      // Update the existing processing message text
      this.messages[processingMessageIndex].text = this.translate.instant('AI_ASSISTANT.SYNCING_CONTEXT');
      this.scrollToBottom();
    } else if (progressMessage === 'context_synchronized_processing') {
      // Update the existing processing message text
      this.messages[processingMessageIndex].text = this.translate.instant('AI_ASSISTANT.CONTEXT_SYNCED');
      this.scrollToBottom();
    }
  }

  handleStreamingUpdate(partialContent: string, processingMessageIndex: number): void {
    const text = this.extractStreamingText(partialContent);
    if (!text) return;

    const msg = this.messages[processingMessageIndex];
    if (!msg) return;

    // Transition from processing state to streaming state
    if (msg.isProcessing) {
      msg.isProcessing = false;
    }
    msg.isStreaming = true;
    msg.text = text;

    // Cancel the processing hint since we're now showing real content
    if (this.hintTimeout) {
      clearTimeout(this.hintTimeout);
      this.hintTimeout = null;
    }
    this.showProcessingHint = false;

    // Auto-show bubble when collapsed and streaming content arrives
    if (this.isCollapsed && !this.showBubble) {
      this.showBubble = true;
      this.saveGlobalState();
    }

    this.scrollToBottom();
  }

  /**
   * The server guarantees partialContent is displayable text, never an
   * envelope. This is only the guard for a server that has not caught up.
   */
  private extractStreamingText(partial: string): string | null {
    const text = (partial || '').trim();
    if (!text || /"(?:responseType|mapData|textData)"\s*:/.test(text)) return null;
    return text;
  }

  handleJobCompletion(job: Job, processingMessageIndex: number): void {
    this.isLoading = false;
    this.pendingJobId = null;
    this.generationStateService.endAssistantTask(job.id);
    this.showProcessingHint = false;
    if (this.hintTimeout) {
      clearTimeout(this.hintTimeout);
      this.hintTimeout = null;
    }

    const sourceMapId = this.sourceMapId;
    const currentMapId = this.mapService.getCurrentSelectedMapFromStore()?.id;
    const isCrossMap = !!sourceMapId && !!currentMapId && currentMapId !== sourceMapId;

    // MAP-1443: when the assistant was closed mid-stream with preview OFF, the
    // user's intent was "auto-apply on completion". Honor it: run through
    // continueJobCompletion (which auto-applies because previewMode=false) and
    // do NOT raise a green "ready" toast. Skipped for cross-map because we'd
    // be applying to the wrong cytoscape; in that case the toast is the right
    // affordance (open the source smap to review).
    const closedWithAutoApply =
      this.isDialogClosing && !this.previewMode && !isCrossMap;

    // Notify when:
    // - assistant was closed mid-stream WITH preview ON (user wants to review), OR
    // - assistant is open but the request was for a different smap.
    const shouldNotify =
      isCrossMap || (this.isDialogClosing && this.previewMode);
    if (shouldNotify) {
      this.showResponseReadyNotification(sourceMapId, isCrossMap, job.id);
    }

    if (closedWithAutoApply) {
      this.sourceMapId = null;
      if (!job.result) {
        this.jobService.cleanup(job.id);
        return;
      }
      // Component is destroyed so DOM updates inside continueJobCompletion are
      // no-ops; only the side effects (executeAction / addDirectly /
      // applyPendingLinks / model refresh) on the live map matter.
      this.continueJobCompletion(job, processingMessageIndex);
      return;
    }

    // Closed with preview ON, or cross-map: skip in-chat rendering — the user
    // will re-open via the notification action.
    if (this.isDialogClosing) {
      this.sourceMapId = null;
      this.jobService.cleanup(job.id);
      return;
    }

    this.sourceMapId = null;

    if (!job.result) {
      return;
    }

    // Simply process the final AI response - progress messages are handled separately via IN_PROGRESS updates
    this.continueJobCompletion(job, processingMessageIndex);

    // Auto-show bubble when collapsed and a new response arrives (AC 3)
    // Always show when collapsed — the user can't see the conversation area
    if (this.isCollapsed && this.latestAssistantMessage) {
      this.showBubble = true;
      this.saveGlobalState();
    }
  }

  private showResponseReadyNotification(sourceMapId: string | null, isCrossMap: boolean, taskKey?: string): void {
    const message = isCrossMap
      ? this.translate.instant('AI_ASSISTANT.RESPONSE_READY_CROSS_MAP')
      : this.translate.instant('AI_ASSISTANT.RESPONSE_READY');
    const actionLabel = isCrossMap
      ? this.translate.instant('CREATE_MAP_DIALOG.OPEN_SMAP')
      : this.translate.instant('AI_ASSISTANT.OPEN_ASSISTANT');
    const wasDialogClosing = this.isDialogClosing;

    this.notificationService.success(message, {
      title: this.translate.instant('AI_ASSISTANT.RESPONSE_READY_TITLE'),
      duration: 0, // Don't auto-dismiss
      taskKey,
      action: {
        label: actionLabel,
        handler: (onClose) => {
          onClose();
          const openSheet = () => this.bottomSheet.open(ChatBottomsheetComponent, {
            panelClass: 'chat-bottomsheet'
          });
          const currentId = this.mapService.getCurrentSelectedMapFromStore()?.id;
          const needsNavigation = !!sourceMapId && currentId !== sourceMapId;

          if (needsNavigation) {
            // Navigate to the source smap. Only (re)open the sheet if the
            // assistant was closed when the job finished — otherwise the
            // sheet is still open and already rendering the response.
            this.router.navigate(['/dashboard', 'map', sourceMapId]).then(() => {
              if (wasDialogClosing) {
                openSheet();
              }
            });
          } else if (wasDialogClosing) {
            openSheet();
          }
        }
      }
    });
  }

  private continueJobCompletion(job: Job, processingMessageIndex: number): void {
    // Extract the nested response data
    const rawData = get(job, 'result.rawData', null);
    if (!rawData) {
      return;
    }
    // Parse the response based on the structure
    let response;
    if (rawData.responseType) {
      // ChatOutput format from backend: {responseType, data, action, ...}
      // Normalise: map 'data' → 'textData' for text responses so downstream extraction works uniformly
      response = rawData;
      if (rawData.responseType === 'text' && typeof rawData.data === 'string') {
        response = {...rawData, textData: rawData.data};
      }
    } else if (rawData.type === 'text' || rawData.text) {
      // Legacy Response API format: {type: 'text', text: '...'}
      const textValue = (rawData.text || '').toString().trim();
      if (textValue.startsWith('{') || textValue.startsWith('[')) {
        try {
          response = JSON.parse(textValue);
        } catch (e) {
          console.error('Failed to parse response JSON:', e);
          response = {responseType: 'text', textData: textValue};
        }
      } else {
        response = {responseType: 'text', textData: textValue};
      }
    } else {
      response = rawData;
    }

    const action = response?.action || 'none';
    const suggestions = response?.suggestions || null;

    // Handle confirmation: user said "do it" → find last message with pendingActions and apply
    if (action === 'apply') {
      const lastPending = [...this.messages].reverse().find(m => m.pendingActions?.length && !m.applied);
      if (lastPending) {
        lastPending.applied = true;
        this.markMessageApplied(lastPending.text);
        const counts = this.emptyChangeCounts();
        for (const a of lastPending.pendingActions!) {
          this.executeAction(a, counts);
        }
        if (this.hasAnyChanges(counts)) {
          this.notificationService.success(this.buildChangeNotification(counts), {duration: 5000});
        }
      }
      const confirmText = this.extractTextFromResponse(response);
      this.messages[processingMessageIndex] = {
        text: confirmText || this.translate.instant('AI_ASSISTANT.SMAP_UPDATED'),
        isSent: false,
        isCanvas: false,
        isUsers: false
      };
      this.scrollToBottom();
      this.loadCreditBalance();
      this.jobService.cleanup(job.id);
      return;
    }

    // Handle create-models (legend generation) - always auto-apply, no preview needed
    if (action === 'create-models') {
      const cleanText = this.extractTextFromResponse(response);
      this.messages[processingMessageIndex] = {
        text: cleanText || this.translate.instant('AI_ASSISTANT.MODELS_CREATED'),
        isSent: false, isCanvas: false, isUsers: false, applied: true
      };
      this.markMessageApplied(this.messages[processingMessageIndex].text);
      // Refresh legend data on the frontend
      this.modelService.modelsChangedSubject.next();
      this.scrollToBottom();
      this.loadCreditBalance();
      this.jobService.cleanup(job.id);
      return;
    }

    // Handle delete-models / recreate-models - same UX as create-models.
    if (action === 'delete-models' || action === 'recreate-models') {
      const cleanText = this.extractTextFromResponse(response);
      const fallback = action === 'delete-models'
        ? this.translate.instant('AI_ASSISTANT.LEGEND_CLEARED')
        : this.translate.instant('AI_ASSISTANT.LEGEND_RECREATED');
      this.messages[processingMessageIndex] = {
        text: cleanText || fallback,
        isSent: false, isCanvas: false, isUsers: false, applied: true
      };
      this.markMessageApplied(this.messages[processingMessageIndex].text);
      this.modelService.modelsChangedSubject.next();
      this.scrollToBottom();
      this.loadCreditBalance();
      this.jobService.cleanup(job.id);
      return;
    }

    // Handle deduplicate
    if (action === 'deduplicate') {
      const cleanText = this.extractTextFromResponse(response);
      if (this.previewMode) {
        this.messages[processingMessageIndex] = {
          text: cleanText || this.translate.instant('AI_ASSISTANT.DEDUP_WILL'),
          isSent: false, isCanvas: false, isUsers: false,
          pendingActions: [{type: 'deduplicate', payload: null}]
        };
      } else {
        this.executeAction({type: 'deduplicate', payload: null});
        this.messages[processingMessageIndex] = {
          text: cleanText || this.translate.instant('AI_ASSISTANT.DEDUP_COMPLETE'),
          isSent: false, isCanvas: false, isUsers: false, applied: true
        };
        this.markMessageApplied(this.messages[processingMessageIndex].text);
      }
      this.scrollToBottom();
      this.loadCreditBalance();
      this.jobService.cleanup(job.id);
      return;
    }

    // Handle rearrange
    if (action === 'rearrange' && (response?.positions?.length || response?.layout)) {
      const cleanText = this.extractTextFromResponse(response);
      const rearrangeAction: PendingAction = response?.layout
        ? {type: 'rearrange_layout', payload: response.layout}
        : {type: 'rearrange', payload: response.positions};
      const summary = response?.layout
        ? this.translate.instant('AI_ASSISTANT.REARRANGED_LAYOUT', {layout: response.layout})
        : this.translate.instant('AI_ASSISTANT.REPOSITIONED_NODES', {count: response.positions.length});

      if (this.previewMode) {
        this.messages[processingMessageIndex] = {
          text: cleanText || summary,
          isSent: false, isCanvas: false, isUsers: false,
          pendingActions: [rearrangeAction]
        };
      } else {
        this.executeAction(rearrangeAction);
        this.messages[processingMessageIndex] = {
          text: cleanText || summary,
          isSent: false, isCanvas: false, isUsers: false, applied: true
        };
        this.markMessageApplied(this.messages[processingMessageIndex].text);
      }
      this.scrollToBottom();
      this.loadCreditBalance();
      this.jobService.cleanup(job.id);
      return;
    }

    // Handle new unified format (mapData) or legacy format (data)
    // Only treat 'data' as map data when it's an object (not the text string from ChatOutput)
    const mapData = response?.mapData || (typeof response?.data === 'object' ? response.data : null);
    const nodesCount = mapData?.nodes?.length || 0;
    const linksCount = mapData?.links?.length || 0;

    // Links-only response (connecting existing nodes)
    if (response?.responseType === 'data' && nodesCount === 0 && linksCount > 0) {
      if (this.previewMode) {
        this.messages[processingMessageIndex] = {
          text: this.translate.instant(
            linksCount === 1 ? 'AI_ASSISTANT.LINKS_TO_CONNECT_SINGULAR' : 'AI_ASSISTANT.LINKS_TO_CONNECT_PLURAL',
            {count: linksCount}),
          isSent: false,
          isCanvas: false,
          isUsers: false,
          pendingActions: [{type: 'add_links', payload: mapData.links}]
        };
      } else {
        this.applyPendingLinks(mapData.links);
        this.messages[processingMessageIndex] = {
          text: this.translate.instant(
            linksCount === 1 ? 'AI_ASSISTANT.LINKS_CREATED_SINGULAR' : 'AI_ASSISTANT.LINKS_CREATED_PLURAL',
            {count: linksCount}),
          isSent: false, isCanvas: false, isUsers: false, applied: true
        };
        this.markMessageApplied(this.messages[processingMessageIndex].text);
      }
      this.scrollToBottom();
      this.loadCreditBalance();
      this.jobService.cleanup(job.id);
      return;
    }

    // Data response with nodes
    if (response && response.responseType === 'data' && mapData && nodesCount > 0) {
      let summaryText = this.translate.instant('AI_ASSISTANT.AI_MAP_SUMMARY_HEADER', {
        nodes: nodesCount,
        links: linksCount
      });

      if (nodesCount > 0) {
        mapData.nodes.slice(0, 3).forEach((node: any, index: any) => {
          const nodeName = node.name || this.translate.instant('AI_ASSISTANT.UNNAMED_NODE');
          const nodeDescription = node.description || this.translate.instant('AI_ASSISTANT.NO_DESCRIPTION');
          summaryText += `${index + 1}. **${nodeName}**: ${nodeDescription}\n`;
        });
      }

      if (this.previewMode) {
        this.messages[processingMessageIndex] = {
          text: summaryText,
          isSent: false,
          isCanvas: true,
          isUsers: false,
          data: this.transformDataForPreview(mapData),
          action,
          suggestions
        };
        this.hasPreview = true;
      } else {
        // Apply directly without preview
        this.messages[processingMessageIndex] = {
          text: summaryText,
          isSent: false,
          isCanvas: false,
          isUsers: false,
          data: this.transformDataForPreview(mapData),
          applied: true
        };
        this.markMessageApplied(this.messages[processingMessageIndex].text);
        this.addDirectly(mapData);
      }
    } else {
      const cleanText = this.extractTextFromResponse(response);
      const message: ChatMessage = {
        text: cleanText,
        isSent: false,
        isCanvas: false,
        isUsers: false
      };

      // Convert actionable suggestions into pendingActions
      if (action === 'suggest' && suggestions?.length) {
        const actionable = suggestions.filter((s: any) =>
          s.targetId && s.changes && (s.changes.name || s.changes.description || s.changes.imageUrl !== undefined || s.changes.backgroundImageUrl !== undefined || s.changes.imageOpacity !== undefined || s.changes.metadata?.length)
        );
        if (actionable.length) {
          if (this.previewMode) {
            message.pendingActions = actionable.map((s: Suggestion) => ({
              type: s.type,
              payload: s.changes,
              targetId: s.targetId,
              targetName: s.targetName
            }));
          } else {
            message.applied = true;
            const counts = this.emptyChangeCounts();
            for (const s of actionable) {
              this.executeAction({
                type: s.type,
                payload: s.changes,
                targetId: s.targetId,
                targetName: s.targetName
              }, counts);
            }
            if (this.hasAnyChanges(counts)) {
              this.notificationService.success(this.buildChangeNotification(counts), {duration: 5000});
            }
          }
        }
      }

      this.messages[processingMessageIndex] = message;
      if (message.applied) {
        this.markMessageApplied(message.text);
      }
    }

    this.scrollToBottom();
    this.loadCreditBalance();
    this.jobService.cleanup(job.id); // Clean up SSE connection after job completion
  }

  handleJobFailure(job: any, processingMessageIndex: number): void {
    this.isLoading = false;
    this.pendingJobId = null;
    if (job?.id) this.generationStateService.endAssistantTask(job.id);
    this.showProcessingHint = false;
    this.sourceMapId = null;
    if (this.hintTimeout) {
      clearTimeout(this.hintTimeout);
      this.hintTimeout = null;
    }

    // If dialog was closed, show error notification
    if (this.isDialogClosing) {
      this.notificationService.error(
        job.error || this.translate.instant('AI_ASSISTANT.REQUEST_ERROR'),
        {
          title: this.translate.instant('AI_ASSISTANT.AI_REQUEST_FAILED_TITLE'),
          duration: 10000,
          taskKey: job?.id
        }
      );
      this.jobService.cleanup(job.id);
      return;
    }

    this.messages[processingMessageIndex] = {
      text: `${job.error}`,
      isSent: false,
      isCanvas: false,
      isUsers: false,
      isError: true
    };

    this.scrollToBottom();
    this.jobService.cleanup(job.id); // Clean up SSE connection after job failure
  }

  private extractTextFromResponse(response: any): string {
    // Check string fields explicitly (empty string is valid text, not a fallback trigger)
    let responseText: any;
    if (typeof response?.textData === 'string') {
      responseText = response.textData;
    } else if (typeof response?.result?.rawData?.text === 'string') {
      responseText = response.result.rawData.text;
    } else if (typeof response?.data === 'string') {
      responseText = response.data;
    } else {
      responseText = response?.textData || response?.result?.rawData?.text || response?.data || response;
    }
    if (typeof responseText === 'object' && responseText !== null) {
      const textFields = ['textData', 'joke', 'message', 'content', 'answer', 'response', 'result', 'explanation', 'text'];
      for (const field of textFields) {
        if (responseText[field] && typeof responseText[field] === 'string') {
          let extracted = responseText[field];
          if (responseText.explanation && field !== 'explanation') {
            extracted += `\n\n*${responseText.explanation}*`;
          }
          responseText = extracted;
          break;
        }
      }
    }
    if (typeof responseText === 'string') {
      return this.unwrapTextJson(responseText).replace(/【[^】]*】/g, '').replace(/​/g, '').trim();
    }
    // Structured response with no text field — fall back to a synthesized
    // summary rather than dumping the raw JSON into the chat.
    const summary = this.summarizeStructuredResponse(responseText);
    return summary ?? '';
  }

  /**
   * Unwrap {"text":"..."} wrapper that the AI sometimes puts inside textData.
   * The structured output schema asks for a plain string but the model
   * occasionally nests it in a {text} object. parseMessageText handles this
   * on reload; this helper does the same for the streaming/completion paths.
   */
  private unwrapTextJson(value: string): string {
    if (!value.startsWith('{')) return value;
    try {
      const parsed = JSON.parse(value);
      if (typeof parsed === 'object' && parsed !== null) {
        // Try common text wrapper keys
        for (const key of ['text', 'textData', 'content', 'message']) {
          if (typeof parsed[key] === 'string') return parsed[key];
        }
      }
    } catch { /* not JSON, return as-is */
    }
    return value;
  }

  private applyPositions(positions: { id: string, x: number, y: number }[]): void {
    const cy = this.cyService.cy;
    if (!cy) return;

    // Always use AI-computed positions — the AI has screenshot + node data + BB center
    positions.forEach(pos => {
      const node = cy.$('#' + pos.id);
      if (node.length) {
        node.animate({
          position: {x: pos.x, y: pos.y}
        }, {
          duration: 800,
          easing: 'ease-in-out-cubic'
        });
      }
    });

    setTimeout(() => {
      this.nodeService.updateNodePositions(positions).subscribe();
      cy.fit(undefined, 50);
    }, 900);
  }

  private runDeduplication(): void {
    const mapId = this.mapService.getCurrentSelectedMapFromStore()?.id;
    if (!mapId) return;

    this.nodeService.deduplicateNodes(mapId).subscribe((res: any) => {
      const cy = this.cyService.cy;
      if (cy && res.removedIds?.length) {
        cy.batch(() => {
          for (const id of res.removedIds) {
            const el = cy.$('#' + id);
            if (el.length) el.remove();
          }
        });
        cy.style().update();
      }
      this.notificationService.success(
        res.mergedCount > 0
          ? this.translate.instant(
              res.mergedCount === 1 ? 'AI_ASSISTANT.MERGED_DUPLICATES_SINGULAR' : 'AI_ASSISTANT.MERGED_DUPLICATES_PLURAL',
              {count: res.mergedCount})
          : this.translate.instant('AI_ASSISTANT.NO_DUPLICATES'),
        {duration: 5000}
      );
    });
  }

  private applyLayoutAlgorithm(layoutName: string): void {
    const cy = this.cyService.cy;
    if (!cy) return;

    const layout = cy.layout({
      name: layoutName,
      animate: true,
      animationDuration: 1200,
      fit: true,
      padding: 50,
      ...(layoutName === 'dagre' ? {rankDir: 'TB', nodeSep: 80, rankSep: 100} : {}),
      ...(layoutName === 'concentric' ? {minNodeSpacing: 60} : {}),
      ...(layoutName === 'cose-bilkent' ? {idealEdgeLength: 120, nodeRepulsion: 6500} : {}),
      ...(layoutName === 'fcose' ? {idealEdgeLength: 100, nodeRepulsion: 5000} : {}),
    } as any);

    layout.on('layoutstop', () => {
      const newPositions: { id: string, x: number, y: number }[] = [];
      cy.nodes().forEach((node: any) => {
        const pos = node.position();
        newPositions.push({id: node.id(), x: Math.round(pos.x), y: Math.round(pos.y)});
      });
      if (newPositions.length) {
        this.nodeService.updateNodePositions(newPositions).subscribe();
      }
    });

    layout.run();
  }

  private applyPendingLinks(links: any[]): void {
    const extractId = (ref: any, depth = 0): string | null => {
      if (!ref) return null;
      if (typeof ref === 'string') return ref;
      if (depth > 3) return null;
      if (typeof ref === 'object' && ref.id) return extractId(ref.id, depth + 1);
      return null;
    };

    const cleanedLinks = links.map((link: any) => ({
      ...link,
      from: extractId(link.from),
      to: extractId(link.to),
      metadata: Array.isArray(link.metadata) ? link.metadata.map((m: any) => {
        const {id, ...rest} = m;
        return rest;
      }) : []
    })).filter((link: any) => link.from && link.to);

    const mapId = this.mapService.getCurrentSelectedMapFromStore()?.id;
    if (!mapId || !cleanedLinks.length) return;

    this.nodeService.createGenerated({nodes: [], links: cleanedLinks, mapId}).subscribe((res: any) => {
      const cy = this.cyService.cy;
      if (cy) {
        cy.batch(() => {
          (res.links || []).forEach((link: any) => {
            const sourceId = typeof link.from === 'object' ? link.from?.id : link.from;
            const targetId = typeof link.to === 'object' ? link.to?.id : link.to;
            if (sourceId && targetId && cy.$('#' + sourceId).length && cy.$('#' + targetId).length) {
              cy.add({
                group: 'edges',
                data: {...link, source: sourceId, target: targetId, label: 'name'}
              });
            }
          });
        });
        cy.style().update();
      }
    });
  }

  hasPendingAction(message: ChatMessage): boolean {
    return !message.applied && !!message.pendingActions?.length;
  }

  applyPendingAction(index: number): void {
    const message = this.messages[index];
    if (!message.pendingActions?.length) return;
    message.applied = true;
    this.markMessageApplied(message.text);

    const counts = this.emptyChangeCounts();
    for (const action of message.pendingActions) {
      this.executeAction(action, counts);
    }
    if (this.hasAnyChanges(counts)) {
      this.notificationService.success(this.buildChangeNotification(counts), {duration: 5000});
    }

    this.messages.push({
      text: this.translate.instant('AI_ASSISTANT.SMAP_UPDATED'),
      isSent: false,
      isCanvas: false,
      isUsers: false
    });
    this.scrollToBottom();
  }

  private executeAction(action: PendingAction, counts?: ChangeCounts): void {
    const cy = this.cyService.cy;
    const currentMap = this.mapService.getCurrentSelectedMapFromStore();

    switch (action.type) {
      case 'rearrange':
        this.applyPositions(action.payload);
        break;

      case 'rearrange_layout':
        this.applyLayoutAlgorithm(action.payload);
        break;

      case 'deduplicate':
        this.runDeduplication();
        break;

      case 'create-models':
      case 'delete-models':
      case 'recreate-models':
        // Already handled server-side during chat execution, just refresh
        this.modelService.modelsChangedSubject.next();
        break;

      case 'add_links':
        this.applyPendingLinks(action.payload);
        if (counts) counts.created.links += Array.isArray(action.payload) ? action.payload.length : 0;
        break;

      case 'update_node':
        if (cy && action.targetId && action.payload) {
          const cyNode = cy.$('#' + action.targetId);
          const nodeData = cyNode?.data();
          if (!nodeData) break;
          const update: any = {id: action.targetId};
          if (action.payload.name) {
            update.name = action.payload.name;
            cyNode.data('name', action.payload.name);
          }
          if (action.payload.description) {
            update.description = action.payload.description;
            cyNode.data('description', action.payload.description);
          }
          if (action.payload.imageUrl !== undefined) {
            const nextImageUrl = action.payload.imageUrl || null;
            update.imageUrl = nextImageUrl;
            cyNode.data('imageUrl', nextImageUrl);
          }
          if (action.payload.metadata) {
            update.metadata = this.mergeMetadataByKey(nodeData.metadata, action.payload.metadata);
            cyNode.data('metadata', update.metadata);
          }
          this.nodeService.updateNode(update, true).subscribe();
          this.syncTappedAfterUpdate(action.targetId, 'node');
          if (counts) counts.updated.nodes += 1;
        }
        break;

      case 'update_link':
        if (cy && action.targetId && action.payload) {
          const cyEdge = cy.$('#' + action.targetId);
          const edgeData = cyEdge?.data();
          if (!edgeData) break;
          const update: any = {id: action.targetId};
          if (action.payload.name) {
            update.name = action.payload.name;
            cyEdge.data('name', action.payload.name);
          }
          if (action.payload.description) {
            update.description = action.payload.description;
            cyEdge.data('description', action.payload.description);
          }
          if (action.payload.metadata) {
            update.metadata = this.mergeMetadataByKey(edgeData.metadata, action.payload.metadata);
            cyEdge.data('metadata', update.metadata);
          }
          this.nodeService.updateLink(update).subscribe();
          this.syncTappedAfterUpdate(action.targetId, 'link');
          if (counts) counts.updated.links += 1;
        }
        break;

      case 'update_map':
        if (currentMap && action.payload) {
          if (action.payload.name) currentMap.name = action.payload.name;
          if (action.payload.description) currentMap.description = action.payload.description;
          if (action.payload.metadata) {
            currentMap.metadata = this.mergeMetadataByKey(currentMap.metadata as any[], action.payload.metadata as any[]);
          }
          // MAP-1533: assistant-set background image + its opacity (styles.opacity).
          if (action.payload.backgroundImageUrl !== undefined || action.payload.imageOpacity !== undefined) {
            currentMap.styles = {...(currentMap.styles || {})};
            if (action.payload.backgroundImageUrl !== undefined) {
              currentMap.styles.backgroundImageUrl = action.payload.backgroundImageUrl || '';
            }
            if (action.payload.imageOpacity !== undefined) {
              currentMap.styles.opacity = action.payload.imageOpacity;
            }
          }
          this.mapService.update(currentMap).subscribe();
        }
        break;
    }

    cy?.style().update();
  }

  private mergeMetadataByKey(existing: any[] | undefined | null, incoming: any[] | undefined | null): any[] {
    const base = Array.isArray(existing) ? existing : [];
    if (!Array.isArray(incoming) || !incoming.length) return base;

    const norm = (k: unknown) => typeof k === 'string' ? k.toLowerCase().trim() : '';
    const incomingByKey = new Map<string, any>();
    for (const item of incoming) {
      const k = norm(item?.key);
      if (k) incomingByKey.set(k, item);
    }

    const merged = base.map(item => {
      const k = norm(item?.key);
      if (k && incomingByKey.has(k)) {
        const inc = incomingByKey.get(k);
        incomingByKey.delete(k);
        // Take only the new value; keep existing id/type/order/editable so TypeORM upserts the row in place
        // instead of inserting a duplicate, and the user's structural choices are preserved.
        return {...item, value: inc?.value};
      }
      return item;
    });

    for (const inc of incomingByKey.values()) merged.push(inc);
    return merged;
  }

  add(index: number): void {
    // Mark as applied immediately to disable button
    this.messages[index].applied = true;
    this.markMessageApplied(this.messages[index].text);

    // No-reload insert: create on backend, add to Cytoscape directly
    const data = this.messages[index].data;
    if (!data) return;

    // Flatten tree structure: collect all nodes and links recursively
    const allNodes: any[] = [];
    const allLinks: any[] = [];
    const flattenNodes = (nodes: any[]) => {
      for (const node of nodes) {
        const {links, children, ...nodeData} = node;
        allNodes.push(nodeData);
        if (links?.length) allLinks.push(...links);
        if (children?.length) flattenNodes(children);
      }
    };
    flattenNodes(data.nodes || []);

    // Also collect top-level links if present
    if (data.links?.length) allLinks.push(...data.links);

    const extractId = (ref: any, depth = 0): string | null => {
      if (!ref) return null;
      if (typeof ref === 'string') return ref;
      if (depth > 3) return null;
      if (typeof ref === 'object' && ref.id) return extractId(ref.id, depth + 1);
      return null;
    };

    const cleanedNodes = allNodes.map((node: any) => ({
      ...node,
      metadata: Array.isArray(node.metadata) ? node.metadata.map((m: any) => {
        const {id, ...rest} = m;
        return rest;
      }) : []
    }));

    // Deduplicate links by id
    const seenLinkIds = new Set<string>();
    const cleanedLinks = allLinks.map((link: any) => ({
      ...link,
      from: extractId(link.from),
      to: extractId(link.to),
      metadata: Array.isArray(link.metadata) ? link.metadata.map((m: any) => {
        const {id, ...rest} = m;
        return rest;
      }) : []
    })).filter((link: any) => {
      if (!link.from || !link.to) return false;
      if (seenLinkIds.has(link.id)) return false;
      seenLinkIds.add(link.id);
      return true;
    });

    const mapId = this.mapService.getCurrentSelectedMapFromStore()?.id;
    if (!mapId) return;

    this.nodeService.createGenerated({nodes: cleanedNodes, links: cleanedLinks, mapId}).subscribe((res: any) => {
      this.addNodesToCytoscape(res);
      this.notificationService.success(this.buildChangeNotificationFromGenerated(res), {duration: 5000});
    });
  }

  private addDirectly(mapData: any): void {
    const extractId = (ref: any, depth = 0): string | null => {
      if (!ref) return null;
      if (typeof ref === 'string') return ref;
      if (depth > 3) return null;
      if (typeof ref === 'object' && ref.id) return extractId(ref.id, depth + 1);
      return null;
    };

    // Flatten nodes (handles both flat and tree structures)
    const allNodes: any[] = [];
    const allLinks: any[] = [];
    const flattenNodes = (nodes: any[]) => {
      for (const node of nodes) {
        const {links, children, ...nodeData} = node;
        allNodes.push(nodeData);
        if (links?.length) allLinks.push(...links);
        if (children?.length) flattenNodes(children);
      }
    };
    flattenNodes(mapData.nodes || []);
    if (mapData.links?.length) allLinks.push(...mapData.links);

    const cleanedNodes = allNodes.map((node: any) => ({
      ...node,
      metadata: Array.isArray(node.metadata) ? node.metadata.map((m: any) => {
        const {id, ...rest} = m;
        return rest;
      }) : []
    }));

    const seenLinkIds = new Set<string>();
    const cleanedLinks = allLinks.map((link: any) => ({
      ...link,
      from: extractId(link.from),
      to: extractId(link.to),
      metadata: Array.isArray(link.metadata) ? link.metadata.map((m: any) => {
        const {id, ...rest} = m;
        return rest;
      }) : []
    })).filter((link: any) => {
      if (!link.from || !link.to) return false;
      if (seenLinkIds.has(link.id)) return false;
      seenLinkIds.add(link.id);
      return true;
    });

    const mapId = this.mapService.getCurrentSelectedMapFromStore()?.id;
    if (!mapId) return;

    this.nodeService.createGenerated({nodes: cleanedNodes, links: cleanedLinks, mapId}).subscribe((res: any) => {
      this.addNodesToCytoscape(res);
      this.notificationService.success(this.buildChangeNotificationFromGenerated(res), {duration: 5000});
    });
  }

  private buildChangeNotification(counts: ChangeCounts): string {
    const phrase = (c: ChangeCount): string => {
      const nodesPart = c.nodes === 1
        ? this.translate.instant('DASHBOARD_MSG.CONFIRM.NODE_SINGULAR')
        : c.nodes > 1 ? this.translate.instant('DASHBOARD_MSG.CONFIRM.NODE_PLURAL', {count: c.nodes}) : '';
      const linksPart = c.links === 1
        ? this.translate.instant('DASHBOARD_MSG.CONFIRM.LINK_SINGULAR')
        : c.links > 1 ? this.translate.instant('DASHBOARD_MSG.CONFIRM.LINK_PLURAL', {count: c.links}) : '';
      if (nodesPart && linksPart) return this.translate.instant('DASHBOARD_MSG.CONFIRM.ELEMENTS_COMBINED', {nodeText: nodesPart, linkText: linksPart});
      if (nodesPart) return nodesPart;
      if (linksPart) return linksPart;
      return this.translate.instant('AI_ASSISTANT.NO_ELEMENTS');
    };

    let msg = this.translate.instant('AI_ASSISTANT.CHANGE_NOTIFICATION', {
      created: phrase(counts.created),
      updated: phrase(counts.updated),
      deleted: phrase(counts.deleted)
    });
    if (/^[a-z]/.test(msg)) {
      msg = msg.charAt(0).toUpperCase() + msg.slice(1);
    }
    return msg;
  }

  private buildChangeNotificationFromGenerated(res: any): string {
    const updatedIds = new Set<string>();
    for (const n of (res.updatedNodes || [])) updatedIds.add(n.id);
    for (const id of Object.keys(res.parentUpdates || {})) updatedIds.add(id);
    return this.buildChangeNotification({
      created: {nodes: res.nodes?.length || 0, links: res.links?.length || 0},
      updated: {nodes: updatedIds.size, links: 0},
      deleted: {nodes: 0, links: 0},
    });
  }

  private emptyChangeCounts(): ChangeCounts {
    return {
      created: {nodes: 0, links: 0},
      updated: {nodes: 0, links: 0},
      deleted: {nodes: 0, links: 0},
    };
  }

  private hasAnyChanges(counts: ChangeCounts): boolean {
    return counts.created.nodes + counts.created.links
      + counts.updated.nodes + counts.updated.links
      + counts.deleted.nodes + counts.deleted.links > 0;
  }

  private syncTappedAfterUpdate(targetId: string, kind: 'node' | 'link'): void {
    const cy = this.cyService.cy;
    if (!cy) return;
    if (kind === 'node' && DashboardComponent.tappedNode?.id === targetId) {
      const cyNode = cy.$('#' + targetId);
      if (cyNode.length) {
        DashboardComponent.tappedNode = {...cyNode.data()};
      }
    }
    if (kind === 'link' && DashboardComponent.tappedLink?.id === targetId) {
      const cyEdge = cy.$('#' + targetId);
      if (cyEdge.length) {
        DashboardComponent.tappedLink = {...cyEdge.data()};
      }
    }
  }

  private addNodesToCytoscape(res: any): void {
    const cy = this.cyService.cy;
    if (!cy) return;

    const mergedIds = new Set(res.mergedNodeIds || []);

    // Separate new nodes: parents first, then children
    const parentNodes = (res.nodes || []).filter((n: any) => !n.parent);
    const childNodes = (res.nodes || []).filter((n: any) => !!n.parent);

    cy.batch(() => {
      // Add new parent nodes
      for (const node of parentNodes) {
        if (!cy.$('#' + node.id).length) {
          cy.add({
            group: 'nodes',
            data: {...node, label: 'name'},
            position: {x: node.x || 0, y: node.y || 0}
          });
        }
      }
      // Add new child nodes with parent reference
      for (const node of childNodes) {
        const parentId = typeof node.parent === 'object' ? node.parent?.id : node.parent;
        if (!cy.$('#' + node.id).length) {
          cy.add({
            group: 'nodes',
            data: {...node, label: 'name', parent: parentId},
            position: {x: node.x || 0, y: node.y || 0}
          });
        }
      }
      // Add new edges (skip if already exists)
      for (const link of (res.links || [])) {
        const sourceId = typeof link.from === 'object' ? link.from?.id : link.from;
        const targetId = typeof link.to === 'object' ? link.to?.id : link.to;
        if (sourceId && targetId && !cy.$('#' + link.id).length) {
          cy.add({
            group: 'edges',
            data: {...link, source: sourceId, target: targetId, label: 'name'}
          });
        }
      }

      // Sync existing nodes that the backend updated (e.g. metadata merged into
      // already-known nodes). Without this the local Cytoscape data — and the
      // sidebar focusedNode reference — stay stale until the page reloads.
      for (const node of (res.updatedNodes || [])) {
        const cyNode = cy.$('#' + node.id);
        if (!cyNode.length) continue;
        if (node.name !== undefined) cyNode.data('name', node.name);
        if (node.description !== undefined) cyNode.data('description', node.description);
        if (node.metadata !== undefined) cyNode.data('metadata', node.metadata);
        if (node.color !== undefined) cyNode.data('color', node.color);
        if (node.imageUrl !== undefined) cyNode.data('imageUrl', node.imageUrl);
      }
    });

    // Move existing nodes to their new parents (backend returned parentUpdates map)
    const parentUpdates: Record<string, string> = res.parentUpdates || {};
    for (const [nodeId, parentId] of Object.entries(parentUpdates)) {
      const cyNode = cy.$('#' + nodeId);
      if (cyNode.length && cy.$('#' + parentId).length) {
        cyNode.move({parent: parentId});
      }
    }

    // If the currently-selected sidebar element was among the updates, force a
    // new reference so node-form's @Input picks up the change without a refresh.
    for (const node of (res.updatedNodes || [])) {
      this.syncTappedAfterUpdate(node.id, 'node');
    }

    cy.style().update();

    // Select new elements and zoom to them
    const allIds = res.allIds || [];
    cy.elements().unselect();
    const newElements = cy.collection();
    allIds.forEach((id: string) => {
      const el = cy.$('#' + id);
      if (el.length) {
        el.select();
        newElements.merge(el);
      }
    });
    if (newElements.length) {
      cy.animate({fit: {eles: newElements, padding: 80}}, {duration: 600});
    }
  }

  parseMessageText(text: string): string {
    // The model sometimes wraps structured output in ```json ... ``` when it
    // answers in text mode. Streaming extraction handles this; reload must too.
    let input = text;
    if (typeof input === 'string') {
      const fenceMatch = input.match(/^\s*```(?:json)?\s*([\s\S]*?)\s*```\s*$/);
      if (fenceMatch) {
        input = fenceMatch[1];
      }
    }
    // Try to parse as JSON first (Response API returns JSON strings)
    try {
      const parsed = JSON.parse(input);
      if (parsed.responseType === 'text' && parsed.textData) {
        // Handle new unified format: {"responseType": "text", "textData": "actual content", "mapData": null}
        return parsed.textData;
      } else if (parsed.responseType === 'text' && parsed.data) {
        // Handle legacy format: {"responseType": "text", "data": "actual content"}
        return parsed.data;
      } else if (parsed.text) {
        // Extract text from Response API format: {"text": "actual content"}
        return parsed.text;
      } else if (typeof parsed === 'string') {
        return parsed;
      } else if (typeof parsed === 'object' && parsed !== null) {
        // Try common text-like fields first.
        const textFields = ['textData', 'joke', 'message', 'content', 'answer', 'response', 'result', 'explanation'];
        for (const field of textFields) {
          if (parsed[field] && typeof parsed[field] === 'string') {
            let result = parsed[field];
            if (parsed.explanation && field !== 'explanation') {
              result += `\n\n*${parsed.explanation}*`;
            }
            return result;
          }
        }
        // Structured action / data responses (rearrange, deduplicate, suggest,
        // mapData payload, links-only) don't always carry a text field. The
        // backend persists the raw AI JSON, so on reload we need to rebuild the
        // human-readable summary we originally showed — otherwise the chat
        // renders raw JSON.
        const summary = this.summarizeStructuredResponse(parsed);
        if (summary) return summary;

        if (parsed.title || parsed.description || parsed.metadata) {
          let metaSummary = '';
          if (parsed.title) metaSummary += `**${parsed.title}**\n\n`;
          if (parsed.description) metaSummary += `${parsed.description}\n\n`;
          if (parsed.metadata && parsed.metadata.length > 0) {
            metaSummary += this.translate.instant('AI_ASSISTANT.METADATA_HEADING') + '\n';
            parsed.metadata.forEach((meta: any) => {
              if (meta.key && meta.value) {
                metaSummary += `- **${meta.key}:** ${meta.value}\n`;
              }
            });
          }
          if (metaSummary.trim()) return metaSummary.trim();
        }
      }
      // No readable form recovered — drop the raw JSON rather than showing it.
      return '';
    } catch {
      // Not JSON — return the fence-stripped form so we don't render ``` markers.
      return input;
    }
  }

  /**
   * Reconstruct the human-readable summary originally produced by
   * `continueJobCompletion` for a given raw assistant response. Used on
   * history reload so structured responses (mapData, rearrange, deduplicate,
   * create-models, links-only) don't render as raw JSON in the bubble or
   * conversation.
   */
  private summarizeStructuredResponse(parsed: any): string | null {
    if (!parsed || typeof parsed !== 'object') return null;

    const action = parsed.action;
    const mapData = parsed.mapData || (typeof parsed.data === 'object' ? parsed.data : null);
    const nodesCount = mapData?.nodes?.length || 0;
    const linksCount = mapData?.links?.length || 0;

    if (parsed.responseType === 'data' && nodesCount === 0 && linksCount > 0) {
      return this.translate.instant(
        linksCount === 1 ? 'AI_ASSISTANT.LINKS_CREATED_SINGULAR' : 'AI_ASSISTANT.LINKS_CREATED_PLURAL',
        {count: linksCount});
    }
    if (parsed.responseType === 'data' && mapData && nodesCount > 0) {
      let summary = this.translate.instant('AI_ASSISTANT.AI_MAP_SUMMARY_HEADER', {
        nodes: nodesCount,
        links: linksCount
      });
      mapData.nodes.slice(0, 3).forEach((node: any, index: number) => {
        const nodeName = node.name || this.translate.instant('AI_ASSISTANT.UNNAMED_NODE');
        const nodeDescription = node.description || this.translate.instant('AI_ASSISTANT.NO_DESCRIPTION');
        summary += `${index + 1}. **${nodeName}**: ${nodeDescription}\n`;
      });
      return summary;
    }
    if (action === 'deduplicate') {
      return this.translate.instant('AI_ASSISTANT.DEDUP_WILL');
    }
    if (action === 'create-models') {
      return this.translate.instant('AI_ASSISTANT.MODELS_CREATED');
    }
    if (action === 'rearrange') {
      if (parsed.layout) {
        return this.translate.instant('AI_ASSISTANT.REARRANGED_LAYOUT', {layout: parsed.layout});
      }
      if (parsed.positions?.length) {
        return this.translate.instant('AI_ASSISTANT.REPOSITIONED_NODES', {count: parsed.positions.length});
      }
    }
    if (action === 'apply') {
      return this.translate.instant('AI_ASSISTANT.SMAP_UPDATED');
    }
    if (action === 'suggest' && Array.isArray(parsed.suggestions) && parsed.suggestions.length) {
      return this.translate.instant(
        parsed.suggestions.length === 1 ? 'AI_ASSISTANT.SUGGESTIONS_SINGULAR' : 'AI_ASSISTANT.SUGGESTIONS_PLURAL',
        {count: parsed.suggestions.length});
    }
    return null;
  }

  cleanMarkdownText(text: string): string {
    // Some responses (streaming or legacy payloads) arrive as JSON strings
    // like {"responseType":"text","textData":"..."} — render the inner text
    // rather than the raw JSON.
    let workingText = text || '';
    const trimmed = workingText.trim();
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        const extracted = parsed?.textData
          ?? parsed?.text
          ?? (typeof parsed?.data === 'string' ? parsed.data : undefined)
          ?? parsed?.content
          ?? parsed?.message;
        if (typeof extracted === 'string') {
          workingText = extracted;
        }
      } catch {
        // Not valid JSON — leave as-is and let markdown handle it
      }
    }

    const cleanText = workingText
      .replace(/【[^】]*】/g, '') // Remove citations
      .replace(/​/g, '') // Remove zero-width spaces
      .trim();

    return MarkdownService.cleanUpMarkdown(cleanText);
  }


  scrollToBottom(): void {
    setTimeout(() => {
      const el = this.messageListRef?.nativeElement?.closest('.bottomsheet-content') as HTMLElement;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
      // Keep the collapsed-state bubble pinned to its latest token. Streaming
      // content grows past the 200px max-height and otherwise sits below the
      // visible area under the fade mask.
      const bubble = this.hostRef?.nativeElement?.querySelector('.bubble-scroll') as HTMLElement | null;
      if (bubble) {
        bubble.scrollTop = bubble.scrollHeight;
      }
    }, 300);
  }

  /**
   * Transform AI response data format to CyPreviewComponent format.
   * AI returns { nodes: [], links: [] } with links having string from/to IDs.
   * CyPreview expects nodes array with links embedded in each node, having from.id/to.id objects.
   */
  private transformDataForPreview(data: { nodes: any[]; links: any[] }): { nodes: any[] } {
    if (!data?.nodes) return {nodes: []};

    // Create a map of node IDs for quick lookup
    const nodeMap = new Map<string, any>();
    data.nodes.forEach(node => {
      nodeMap.set(node.id, {
        ...node,
        links: [],
        children: [],
        properties: node.properties || {}
      });
    });

    // Build parent-child tree from parentId references
    const rootNodes: any[] = [];
    for (const node of nodeMap.values()) {
      if (node.parentId && nodeMap.has(node.parentId)) {
        const parent = nodeMap.get(node.parentId);
        parent.children.push(node);
      } else {
        rootNodes.push(node);
      }
    }

    // Attach links to the source node — CyPreview iterates node.links for edges
    if (data.links) {
      data.links.forEach(link => {
        const sourceId = typeof link.from === 'string' ? link.from : link.from?.id;
        const targetId = typeof link.to === 'string' ? link.to : link.to?.id;

        const sourceNode = nodeMap.get(sourceId);

        if (sourceNode && sourceId && targetId) {
          sourceNode.links.push({
            ...link,
            from: {id: sourceId},
            to: {id: targetId}
          });
        }
      });
    }

    // Return only root nodes — cy-preview will recursively process children
    return {nodes: rootNodes};
  }
}


